
import React, { useState, useRef, useCallback } from "react";
import Badge from "@mui/material/Badge";
import CloseIcon from "@mui/icons-material/Close";

const BUTTON_SIZE = 60;
const DRAG_THRESHOLD = 1;

export default function OperationsAssistantFAB({
  badgeCount,
  onClick,
  position,
  onPositionChange,
  // Branding overrides so a second assistant (Lumi, setup mode) is
  // distinguishable from Rovis. Defaults keep the Rovis FAB unchanged.
  title = "Open Rovis (Operations Assistant)",
  hideTitle = "Hide Rovis",
  label = null,
}) {
  const [localPos, setLocalPos] = useState(null);
  const [hidden, setHidden] = useState(false);
  const localPosRef = useRef(null);
  const dragRef = useRef({
    isDragging: false,
    startX: 0,
    startY: 0,
    startRight: 0,
    startBottom: 0,
    didDrag: false,
  });

  const handlePointerDown = useCallback(
    (e) => {
      dragRef.current = {
        isDragging: true,
        startX: e.clientX,
        startY: e.clientY,
        startRight: position.right,
        startBottom: position.bottom,
        didDrag: false,
      };
      localPosRef.current = null;
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [position]
  );

  const handlePointerMove = useCallback((e) => {
    const drag = dragRef.current;
    if (!drag.isDragging) return;

    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;

    if (!drag.didDrag && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) {
      return;
    }
    drag.didDrag = true;

    const maxRight = window.innerWidth - BUTTON_SIZE;
    const maxBottom = window.innerHeight - BUTTON_SIZE;

    const nextPos = {
      right: Math.min(maxRight, Math.max(0, drag.startRight - dx)),
      bottom: Math.min(maxBottom, Math.max(0, drag.startBottom - dy)),
    };

    localPosRef.current = nextPos;
    setLocalPos(nextPos);
  }, []);

  const handlePointerUp = useCallback(
    (e) => {
      const didDrag = dragRef.current.didDrag;
      const finalPos = localPosRef.current;
      dragRef.current.isDragging = false;
      if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      if (didDrag && finalPos) {
        onPositionChange(finalPos);
        localPosRef.current = null;
        setLocalPos(null);
      } else {
        localPosRef.current = null;
        setLocalPos(null);
        onClick();
      }
    },
    [onClick, onPositionChange]
  );

  const raw = localPos || position;
  const displayPos = {
    right: Math.min(raw.right, window.innerWidth - BUTTON_SIZE),
    bottom: Math.min(raw.bottom, window.innerHeight - BUTTON_SIZE),
  };

  if (hidden) return null;

  return (
    <div
      className="fixed z-[1400] group"
      style={{ bottom: displayPos.bottom, right: displayPos.right }}
    >
      <button
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        className="flex items-center justify-center w-[60px] h-[60px] overflow-visible"
        style={{ touchAction: "none" }}
        title={title}
      >
        <Badge
          badgeContent={badgeCount || 0}
          color="error"
          max={99}
          invisible={!badgeCount}
          overlap="circular"
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          sx={{
            overflow: "visible",
            "& .MuiBadge-badge": {
              zIndex: 30,
              top: 5,
              right: 3,
            },
          }}
        >
          <span className="relative inline-flex h-[60px] w-[60px] items-start justify-center overflow-visible pt-0.5">
            <span
              aria-hidden="true"
              className="absolute bottom-[4px] left-1/2 h-4 w-11 -translate-x-1/2 rounded-full bg-violet-950/55 blur-md"
            />
            <img
              src="/images/mascot-floating.png"
              alt=""
              aria-hidden="true"
              draggable={false}
              className="relative z-10 h-14 w-14 object-contain pointer-events-none select-none [filter:drop-shadow(0_12px_16px_rgba(76,29,149,0.16))]"
            />
            {label && (
              <span
                aria-hidden="true"
                className="absolute bottom-0 left-1/2 z-20 -translate-x-1/2 rounded-full bg-violet-700 px-2 py-0.5 text-[10px] font-semibold leading-none text-white shadow pointer-events-none select-none"
              >
                {label}
              </span>
            )}
          </span>
        </Badge>
      </button>
      <button
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          setHidden(true);
        }}
        className="absolute left-0 -top-1 z-40 flex h-5 w-5 items-center justify-center rounded-full bg-gray-700 text-white opacity-0 shadow transition-opacity pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto hover:bg-gray-800"
        title={hideTitle}
        aria-label={hideTitle}
      >
        <CloseIcon sx={{ fontSize: 11 }} />
      </button>
    </div>
  );
}
